import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, TextField, Switch, Alert, Spinner, Breadcrumbs, PanelCard, Barcode, Chip } from '@glplus/glpweb-dsl';
import { useVendor } from '../../lib/CapabilitiesContext';
import {
  vendorApiClient,
  type ChangeField,
  type ChangeRequest,
  type ProductDetail as Detail,
  type ProductLocationPrice,
  type ProductSpanishInput,
} from '../../lib/vendorApiClient';
import { todayDay } from '../../lib/money';
import { notifyProductsChanged } from '../../lib/productEvents';
import { checkRows, copyRowToAll, overallStatus, rowsDirty, toRowForm, withSaleStart, type PriceRowForm, type PriceRowProblem } from '../../lib/productForm';
import { BORDER, GREEN_INK, HEADING, MUTED, ORANGE_INK, RED, FieldLabel, FlagChip, ProductStatusChip, ProductThumb, departmentPath, describeProductError } from './parts';
import RequestChangeDialog from './RequestChangeDialog';
import BlockPanel from './BlockPanel';

/**
 * P2 — Product detail (Figma `fVTA2OnSEz4UOdTeoXeByH` 55:91).
 *
 * Two halves with different owners (§15l): prices, sales, stock and shown are
 * the vendor's, per location, saved with no review; what the product IS is
 * shared with shoppers and recipes, so it is read-only here and changed by
 * request. Spanish can be added any time (§15b).
 */

const PRICE_GRID = { xs: '1fr', md: '2fr 1fr 1fr 1.3fr 1.3fr 0.7fr 0.7fr' };

const SPANISH_KEYS = ['itemNameEs', 'descriptionShortEs', 'descriptionLongEs', 'ingredientsTextEs'] as const;
type SpanishKey = (typeof SPANISH_KEYS)[number];
const SPANISH_FIELD: Record<SpanishKey, ChangeField> = {
  itemNameEs: 'name_es',
  descriptionShortEs: 'description_short_es',
  descriptionLongEs: 'description_long_es',
  ingredientsTextEs: 'ingredients_es',
};

export default function ProductDetail() {
  const { t, i18n } = useTranslation();
  const { vendorProductId = '' } = useParams();
  const navigate = useNavigate();
  const { activeVendorId, has } = useVendor();
  const canEdit = has('catalog:edit');
  const canResolve = has('confirmation:resolve');

  const [detail, setDetail] = useState<Detail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rows, setRows] = useState<PriceRowForm[]>([]);
  const [problems, setProblems] = useState<Record<string, PriceRowProblem>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [requestField, setRequestField] = useState<ChangeField | null>(null);
  const [sku, setSku] = useState('');
  const [spanishDraft, setSpanishDraft] = useState<Record<SpanishKey, string> | null>(null);
  const [reply, setReply] = useState('');

  const accept = useCallback((d: Detail) => {
    setDetail(d);
    setRows(d.locations.map(toRowForm));
    setProblems({});
    setSku(d.vendorSku ?? '');
  }, []);

  const load = useCallback(() => {
    if (!activeVendorId || !vendorProductId) return;
    setLoadError(null);
    vendorApiClient
      .getProduct(activeVendorId, vendorProductId)
      .then(accept)
      .catch((e: Error) => setLoadError(describeProductError(e, t)));
  }, [activeVendorId, vendorProductId, accept, t]);

  useEffect(() => {
    setDetail(null);
    setNotice(null);
    setError(null);
    setSpanishDraft(null);
    setReply('');
    load();
  }, [load]);

  const dirty = useMemo(() => (detail ? rowsDirty(rows, detail.locations) : false), [rows, detail]);

  if (loadError && !detail) return <Alert severity="error">{loadError}</Alert>;
  if (!detail || !activeVendorId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  const info = detail.info;
  const openRequest = (field: ChangeField) => detail.changeRequests.find((r) => r.state === 'open' && r.field === field) ?? null;
  const lastRefusal = (field: ChangeField) => {
    const latest = detail.changeRequests.find((r) => r.field === field);
    return latest?.state === 'rejected' ? latest : null;
  };

  const setRow = (storeId: string, patch: Partial<PriceRowForm>) => {
    setRows((prev) => prev.map((r) => (r.storeId === storeId ? { ...r, ...patch } : r)));
    setNotice(null);
  };

  const run = async (work: () => Promise<Detail>, done: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      accept(await work());
      setNotice(done);
      return true;
    } catch (e) {
      setError(describeProductError(e, t));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const savePrices = () => {
    const check = checkRows(rows, todayDay());
    if (!check.ok) {
      setProblems(check.problems);
      setError(t('products.detail.fixPrices'));
      return;
    }
    setProblems({});
    void run(() => vendorApiClient.saveProductPrices(activeVendorId, detail.vendorProductId, check.input), t('products.detail.pricesSaved'));
  };

  const saveSku = () =>
    void run(() => vendorApiClient.updateProduct(activeVendorId, detail.vendorProductId, { vendorSku: sku.trim() || null }), t('products.detail.skuSaved'));

  const saveSpanish = async () => {
    if (!spanishDraft) return;
    const changed: ProductSpanishInput = {};
    for (const key of SPANISH_KEYS) {
      const next = spanishDraft[key].trim();
      if (next && next !== (detail.spanish[key] ?? '')) changed[key] = next;
    }
    if (Object.keys(changed).length === 0) return setSpanishDraft(null);
    const sentForReview = !detail.spanish.direct && SPANISH_KEYS.some((k) => changed[k] && detail.spanish[k]);
    const ok = await run(
      () => vendorApiClient.updateProduct(activeVendorId, detail.vendorProductId, { spanish: changed }),
      t(sentForReview ? 'products.detail.spanishSentForReview' : 'products.detail.spanishSaved'),
    );
    if (ok) setSpanishDraft(null);
  };

  const resubmit = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await vendorApiClient.resubmitProduct(activeVendorId, detail.vendorProductId, reply.trim() || undefined);
      setReply('');
      setNotice(t('products.detail.review.resent'));
      notifyProductsChanged();
      load();
    } catch (e) {
      setError(describeProductError(e, t));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (request: ChangeRequest) => {
    setBusy(true);
    setError(null);
    try {
      await vendorApiClient.withdrawChangeRequest(activeVendorId, request.requestId);
      setNotice(t('products.detail.requestWithdrawn'));
      load();
    } catch (e) {
      setError(describeProductError(e, t));
    } finally {
      setBusy(false);
    }
  };

  const day = (iso: string) => new Date(iso).toLocaleDateString(i18n.language.startsWith('es') ? 'es-US' : 'en-US', { month: 'short', day: 'numeric' });

  const requestNotes = (field: ChangeField) => {
    const open = openRequest(field);
    const refused = open ? null : lastRefusal(field);
    return (
      <>
        {open && (
          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }} data-testid={`product-pending-${field}`}>
            <Typography sx={{ fontSize: 13, color: ORANGE_INK, fontWeight: 700 }}>{t('products.detail.pending', { date: day(open.createdAt) })}</Typography>
            {canEdit && (
              <Button
                ariaLabel={t('products.detail.withdrawAria', { field: t(`products.request.field.${field}`) })}
                buttonText={t('products.detail.withdraw')}
                variant="text"
                disabled={busy}
                onClick={() => void withdraw(open)}
                dataTestId={`product-withdraw-${field}`}
                sx={{ p: 0, minWidth: 0, fontSize: 13 }}
              />
            )}
          </Box>
        )}
        {refused && (
          <Typography sx={{ mt: 0.5, fontSize: 13, color: RED }} data-testid={`product-refused-${field}`}>
            {refused.decisionNote ? t('products.detail.refusedWithNote', { note: refused.decisionNote }) : t('products.detail.refused')}
          </Typography>
        )}
      </>
    );
  };

  const infoRow = (field: ChangeField, value: string | null) => (
    <Box sx={{ py: 1.75, borderBottom: `1px solid ${BORDER}` }} data-testid={`product-info-${field}`}>
      <FieldLabel>{t(`products.request.field.${field}`)}</FieldLabel>
      <Typography sx={{ mt: 0.5, fontSize: 15, color: value ? HEADING : MUTED, whiteSpace: 'pre-wrap' }}>{value || t('products.detail.notAdded')}</Typography>
      {requestNotes(field)}
    </Box>
  );

  const first = detail.locations[0];
  const status = overallStatus(detail);
  // While a question is open the product row is only our guess at what this
  // is. The page is about what the VENDOR sent, and shows nothing of the guess
  // outside the question itself.
  const block = detail.block;
  const sent = block?.sent ?? null;
  const title = block ? (sent?.name ?? info.name) : info.name;
  const meta = block
    ? sent
      ? sent.rowNumber == null
        ? t('products.block.addedTyped', { date: day(sent.addedAt) })
        : t('products.block.addedRow', { date: day(sent.addedAt), row: sent.rowNumber })
      : ''
    : [info.brand, departmentPath(t, i18n.language, info.department, info.category)].filter(Boolean).join(' · ');
  const isOwn = detail.barcode.kind === 'assigned';

  return (
    <Box>
      <Breadcrumbs
        ariaLabel={t('products.detail.breadcrumbAria')}
        linkComponent={Link}
        items={[
          { label: t('nav.products'), to: '/products' },
          ...(block ? [{ label: t(`products.filter.${status}`), to: `/products?status=${status}` }] : []),
          { label: title, ariaCurrent: true },
        ]}
      />

      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <ProductThumb src={block ? null : info.imageUrl} size={72} />
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography component="h1" sx={{ fontSize: 32, lineHeight: 1.2, fontWeight: 700, color: HEADING }} data-testid="product-name">
            {title}
          </Typography>
          <Box sx={{ mt: 0.75, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }} data-testid="product-status">
            <ProductStatusChip status={status} />
            {detail.recalled && !block && <FlagChip kind="recalled" />}
            {meta && <Typography sx={{ fontSize: 13, color: MUTED }}>{meta}</Typography>}
          </Box>
        </Box>
        {canEdit && !block && (
          <Button
            ariaLabel={t('products.request.title')}
            buttonText={t('products.request.title')}
            variant="outlinedGreen"
            onClick={() => setRequestField('name')}
            dataTestId="product-request-open"
          />
        )}
      </Box>

      {detail.recalled && !block && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="product-recalled">
          {t('products.detail.recalled')}{' '}
          <Box component={Link} to="/recalls" sx={{ color: 'inherit', fontWeight: 700 }}>
            {t('nav.recalls')}
          </Box>
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="product-error">
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setNotice(null)} data-testid="product-notice">
          {notice}
        </Alert>
      )}

      {!block && detail.review.status === 'pending' && (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="product-review-pending">
          {t('products.detail.review.pending')}
        </Alert>
      )}
      {!block && detail.review.status === 'returned' && (
        <Alert severity="error" title={t('products.detail.review.returnedTitle')} sx={{ mt: 2 }} data-testid="product-review-returned">
          <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap' }} data-testid="product-review-note">
            {detail.review.note ? t('products.detail.review.note', { note: detail.review.note }) : t('products.detail.review.noNote')}
          </Typography>
          {canEdit && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <TextField
                  ariaLabel={t('products.detail.review.reply')}
                  label={t('products.detail.review.reply')}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  multiline
                  minRows={2}
                  disabled={busy}
                  size="small"
                  fullWidth
                  helperText={t('products.detail.review.replyHint')}
                  slotProps={{ htmlInput: { maxLength: 1000 } }}
                  dataTestId="product-review-reply"
                />
              </Box>
              <Button
                ariaLabel={t('products.detail.review.sendAgain')}
                buttonText={t('products.detail.review.sendAgain')}
                variant="contained"
                color="primary"
                disabled={busy}
                onClick={() => void resubmit()}
                dataTestId="product-review-send-again"
              />
            </Box>
          )}
        </Alert>
      )}

      {block && (
        <BlockPanel
          vendorId={activeVendorId}
          vendorProductId={detail.vendorProductId}
          block={block}
          canResolve={canResolve}
          language={i18n.language}
          onChanged={(text) => {
            setError(null);
            load();
            setNotice(text);
          }}
        />
      )}

      <PanelCard accent="#F67A1A" title={t(block ? 'products.block.prices.title' : 'products.detail.prices.title')} sx={{ height: 'auto', mt: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography sx={{ flex: 1, minWidth: 240, fontSize: 15, color: MUTED }}>
            {t(block ? 'products.block.prices.intro' : 'products.detail.prices.intro')}
          </Typography>
          {canEdit && first && rows.length > 1 && (
            <Button
              ariaLabel={t('products.detail.prices.copyAll', { location: first.name })}
              buttonText={t('products.detail.prices.copyAll', { location: first.name })}
              variant="text"
              disabled={busy}
              onClick={() => setRows((prev) => copyRowToAll(prev, first.storeId))}
              dataTestId="product-copy-all"
            />
          )}
        </Box>

        {detail.locations.length === 0 ? (
          <Typography sx={{ mt: 2, fontSize: 15, color: HEADING }} data-testid="product-no-locations">
            {t('products.detail.prices.noLocations')}{' '}
            <Box component={Link} to="/locations/new" sx={{ color: GREEN_INK, fontWeight: 700 }}>
              {t('products.detail.prices.addLocation')}
            </Box>
          </Typography>
        ) : (
          <Box role="table" aria-label={t('products.detail.prices.title')} sx={{ mt: 2 }}>
            <Box role="row" sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: PRICE_GRID, gap: 2, pb: 1, borderBottom: `1px solid ${BORDER}` }}>
              {(['location', 'price', 'sale', 'saleStarts', 'saleEnds', 'inStock', 'shown'] as const).map((c) => (
                <Typography key={c} role="columnheader" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: MUTED, textTransform: 'uppercase' }}>
                  {t(`products.detail.prices.col.${c}`)}
                </Typography>
              ))}
            </Box>
            {detail.locations.map((l) => {
              const row = rows.find((r) => r.storeId === l.storeId);
              if (!row) return null;
              return (
                <PriceRow
                  key={l.storeId}
                  location={l}
                  row={row}
                  problem={problems[l.storeId]}
                  disabled={!canEdit || busy}
                  onChange={(patch) => setRow(l.storeId, patch)}
                />
              );
            })}
            <Typography sx={{ mt: 1.5, fontSize: 13, color: MUTED }}>{t('products.detail.prices.footnote')}</Typography>
            {canEdit && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Button
                  ariaLabel={t('products.detail.prices.discard')}
                  buttonText={t('products.detail.prices.discard')}
                  variant="text"
                  disabled={busy || !dirty}
                  onClick={() => {
                    setRows(detail.locations.map(toRowForm));
                    setProblems({});
                    setError(null);
                  }}
                  dataTestId="product-prices-discard"
                />
                <Button
                  ariaLabel={t('products.detail.prices.save')}
                  buttonText={t('products.detail.prices.save')}
                  variant="contained"
                  color="primary"
                  disabled={busy || !dirty}
                  onClick={savePrices}
                  dataTestId="product-prices-save"
                />
              </Box>
            )}
          </Box>
        )}
      </PanelCard>

      {!block && (
      <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.7fr 1fr' }, gap: 3, alignItems: 'start' }}>
        <PanelCard accent="#343F5B" title={t('products.detail.info.title')} sx={{ height: 'auto' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Typography sx={{ flex: 1, fontSize: 15, color: MUTED }}>{t('products.detail.info.intro')}</Typography>
            <Chip label={t('products.detail.info.locked')} size="small" backgroundColor="#F1F2F4" textColor="#3C4650" fontSize={12} fontWeight={700} />
          </Box>

          <Box sx={{ mt: 2, pb: 1.75, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', borderBottom: `1px solid ${BORDER}` }} data-testid="product-info-photo">
            <ProductThumb src={info.imageUrl} size={96} />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <FieldLabel>{t('products.request.field.photo')}</FieldLabel>
              <Typography sx={{ mt: 0.5, fontSize: 15, fontWeight: 700, color: HEADING }}>
                {t(info.imageIsVendors ? 'products.detail.info.photoYours' : info.imageUrl ? 'products.detail.info.photoStandard' : 'products.detail.info.photoNone')}
              </Typography>
              {!info.imageIsVendors && <Typography sx={{ fontSize: 13, color: MUTED }}>{t('products.detail.info.photoHint')}</Typography>}
              {requestNotes('photo')}
            </Box>
            {canEdit && (
              <Button
                ariaLabel={t('products.detail.info.uploadPhoto')}
                buttonText={t('products.detail.info.uploadPhoto')}
                variant="outlinedGreen"
                disabled={busy}
                onClick={() => setRequestField('photo')}
                dataTestId="product-upload-photo"
              />
            )}
          </Box>

          {infoRow('name', info.name)}
          {infoRow('department_category', departmentPath(t, i18n.language, info.department, info.category, info.subCategory))}
          {infoRow('size', info.size)}
          {infoRow('description_short', info.descriptionShort)}
          {infoRow('description_long', info.descriptionLong)}
          {infoRow('ingredients', info.ingredients)}
          {infoRow('nutrition_label', info.nutritionOnFile ? t('products.detail.nutritionOnFile') : null)}
          {openRequest('other') && infoRow('other', openRequest('other')?.proposedValue ?? null)}

          {canEdit && (
            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography sx={{ flex: 1, minWidth: 200, fontSize: 13, color: MUTED }}>{t('products.detail.info.wrong')}</Typography>
              <Button
                ariaLabel={t('products.request.title')}
                buttonText={t('products.request.title')}
                variant="outlinedGreen"
                disabled={busy}
                onClick={() => setRequestField('name')}
              />
            </Box>
          )}
        </PanelCard>

        <Box sx={{ display: 'grid', gap: 3 }}>
          <PanelCard accent="#343F5B" title={t('products.detail.identity.title')} sx={{ height: 'auto' }}>
            <Typography sx={{ fontSize: 15, color: MUTED }}>{t('products.detail.identity.intro')}</Typography>
            <Box sx={{ py: 1.75, borderBottom: `1px solid ${BORDER}` }}>
              <FieldLabel>{t('products.detail.identity.glpc')}</FieldLabel>
              <Typography sx={{ mt: 0.5, fontSize: 15, color: HEADING }} data-testid="product-glpc">
                {detail.glpProductId}
              </Typography>
            </Box>
            <Box sx={{ py: 1.75, borderBottom: `1px solid ${BORDER}` }} data-testid={`product-barcode-${detail.barcode.kind}`}>
              <FieldLabel>{t('products.detail.identity.barcode')}</FieldLabel>
              {detail.barcode.kind === 'assigned' ? (
                <>
                  <Typography sx={{ mt: 0.5, fontSize: 15, color: HEADING }}>{detail.barcode.value}</Typography>
                  <Typography sx={{ fontSize: 13, color: MUTED }}>{t('products.detail.identity.assigned')}</Typography>
                  <Barcode value={detail.barcode.value} format="auto" height={48} ariaLabel={t('products.detail.identity.barcodeAria', { barcode: detail.barcode.value })} sx={{ mt: 1 }} />
                </>
              ) : detail.barcode.kind === 'own' ? (
                <>
                  <Typography sx={{ mt: 0.5, fontSize: 15, color: HEADING }} data-testid="product-barcode-value">
                    {detail.barcode.value}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: MUTED }}>{t('products.detail.identity.own')}</Typography>
                </>
              ) : detail.barcode.kind === 'shared' ? (
                <Typography sx={{ mt: 0.5, fontSize: 15, color: HEADING }}>{t('products.detail.identity.shared', { glpc: detail.glpProductId })}</Typography>
              ) : (
                <Typography sx={{ mt: 0.5, fontSize: 15, color: MUTED }}>{t('products.detail.identity.none')}</Typography>
              )}
            </Box>
            <Box sx={{ pt: 1.75 }}>
              <TextField
                ariaLabel={t('products.detail.identity.sku')}
                label={t('products.detail.identity.sku')}
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                disabled={!canEdit || busy}
                size="small"
                fullWidth
                slotProps={{ htmlInput: { maxLength: 64 } }}
                dataTestId="product-sku"
              />
              {canEdit && sku.trim() !== (detail.vendorSku ?? '') && (
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    ariaLabel={t('products.detail.identity.saveSku')}
                    buttonText={t('products.detail.identity.saveSku')}
                    variant="contained"
                    color="primary"
                    disabled={busy}
                    onClick={saveSku}
                    dataTestId="product-sku-save"
                  />
                </Box>
              )}
            </Box>
          </PanelCard>

          {(detail.sizes.length > 1 || (isOwn && canEdit)) && (
            <PanelCard accent="#343F5B" title={t('products.detail.sizes.title')} sx={{ height: 'auto' }}>
              <Typography sx={{ fontSize: 15, color: MUTED, mb: 1 }}>{t('products.detail.sizes.intro')}</Typography>
              {detail.sizes.map((s) => {
                const viewing = s.vendorProductId === detail.vendorProductId;
                return (
                  <Box
                    key={s.vendorProductId}
                    component={viewing ? 'div' : Link}
                    {...(viewing ? {} : { to: `/products/${s.vendorProductId}` })}
                    data-testid={`product-size-${s.vendorProductId}`}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25, borderRadius: '8px', textDecoration: 'none', bgcolor: viewing ? '#FEF1E7' : 'transparent' }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING }}>{[s.name, s.size].filter(Boolean).join(' · ')}</Typography>
                      {s.assignedBarcode && <Typography sx={{ fontSize: 12, color: MUTED }}>{s.assignedBarcode}</Typography>}
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: viewing ? ORANGE_INK : GREEN_INK }}>
                      {t(viewing ? 'products.detail.sizes.viewing' : 'products.detail.sizes.open')}
                    </Typography>
                  </Box>
                );
              })}
              {isOwn && canEdit && (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    ariaLabel={t('products.detail.sizes.add')}
                    buttonText={t('products.detail.sizes.add')}
                    variant="outlinedGreen"
                    onClick={() => void navigate(`/products/add/new?sizeOf=${detail.vendorProductId}`)}
                    dataTestId="product-add-size"
                  />
                </Box>
              )}
            </PanelCard>
          )}

          <PanelCard accent="#343F5B" title={t('products.detail.spanish.title')} sx={{ height: 'auto' }}>
            <Typography sx={{ fontSize: 15, color: MUTED }}>{t(detail.spanish.direct ? 'products.detail.spanish.introDirect' : 'products.detail.spanish.introShared')}</Typography>
            {SPANISH_KEYS.map((key) =>
              spanishDraft ? (
                <Box key={key} sx={{ mt: 2 }}>
                  <TextField
                    ariaLabel={t(`products.detail.spanish.${key}`)}
                    label={t(`products.detail.spanish.${key}`)}
                    value={spanishDraft[key]}
                    onChange={(e) => setSpanishDraft((prev) => (prev ? { ...prev, [key]: e.target.value } : prev))}
                    multiline={key !== 'itemNameEs'}
                    minRows={key === 'itemNameEs' ? undefined : 2}
                    disabled={busy}
                    size="small"
                    fullWidth
                    slotProps={{ htmlInput: { maxLength: key === 'itemNameEs' ? 200 : key === 'descriptionShortEs' ? 500 : 5000, lang: 'es' } }}
                    dataTestId={`product-spanish-${key}`}
                  />
                </Box>
              ) : (
                <Box key={key} sx={{ py: 1.75, borderBottom: `1px solid ${BORDER}` }} data-testid={`product-spanish-view-${key}`}>
                  <FieldLabel>{t(`products.detail.spanish.${key}`)}</FieldLabel>
                  <Typography lang="es" sx={{ mt: 0.5, fontSize: 15, color: detail.spanish[key] ? HEADING : MUTED, whiteSpace: 'pre-wrap' }}>
                    {detail.spanish[key] || t('products.detail.notAdded')}
                  </Typography>
                  {requestNotes(SPANISH_FIELD[key])}
                </Box>
              ),
            )}
            {canEdit && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1.5 }}>
                {spanishDraft ? (
                  <>
                    <Button
                      ariaLabel={t('products.detail.spanish.save')}
                      buttonText={t('products.detail.spanish.save')}
                      variant="contained"
                      color="primary"
                      disabled={busy}
                      onClick={() => void saveSpanish()}
                      dataTestId="product-spanish-save"
                    />
                    <Button ariaLabel={t('common.cancel')} buttonText={t('common.cancel')} variant="text" disabled={busy} onClick={() => setSpanishDraft(null)} />
                  </>
                ) : (
                  <Button
                    ariaLabel={t('products.detail.spanish.edit')}
                    buttonText={t('products.detail.spanish.edit')}
                    variant="outlinedGreen"
                    disabled={busy}
                    onClick={() =>
                      setSpanishDraft({
                        itemNameEs: detail.spanish.itemNameEs ?? '',
                        descriptionShortEs: detail.spanish.descriptionShortEs ?? '',
                        descriptionLongEs: detail.spanish.descriptionLongEs ?? '',
                        ingredientsTextEs: detail.spanish.ingredientsTextEs ?? '',
                      })
                    }
                    dataTestId="product-spanish-edit"
                  />
                )}
              </Box>
            )}
          </PanelCard>
        </Box>
      </Box>
      )}

      {requestField && (
        <RequestChangeDialog
          vendorId={activeVendorId}
          detail={detail}
          initialField={requestField}
          onClose={() => setRequestField(null)}
          onSent={() => {
            setRequestField(null);
            setNotice(t('products.detail.requestSent'));
            load();
          }}
        />
      )}
    </Box>
  );
}

function PriceRow({
  location,
  row,
  problem,
  disabled,
  onChange,
}: {
  location: ProductLocationPrice;
  row: PriceRowForm;
  problem: PriceRowProblem | undefined;
  disabled: boolean;
  onChange: (patch: Partial<PriceRowForm>) => void;
}) {
  const { t } = useTranslation();
  const hasPrice = row.price.trim() !== '';
  const hasSale = row.sale.trim() !== '';
  const note = !hasPrice
    ? { text: t('products.notSoldHere'), color: MUTED }
    : !row.shown
      ? { text: t('products.detail.prices.hiddenHere'), color: MUTED }
      : location.status !== 'live'
        ? { text: t('products.detail.prices.waitingForLocation'), color: ORANGE_INK }
        : null;

  return (
    <Box
      role="row"
      data-testid={`product-price-row-${location.storeId}`}
      sx={{ display: 'grid', gridTemplateColumns: PRICE_GRID, gap: 2, alignItems: 'center', py: 1.75, borderBottom: `1px solid ${BORDER}` }}
    >
      <Box role="cell">
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING }}>{location.name}</Typography>
        {note && <Typography sx={{ fontSize: 13, color: note.color }}>{note.text}</Typography>}
        {problem && (
          <Typography role="alert" sx={{ fontSize: 13, color: RED, fontWeight: 700 }} data-testid={`product-price-problem-${location.storeId}`}>
            {t(`products.errors.${problem}`)}
          </Typography>
        )}
      </Box>
      <Box role="cell">
        <TextField
          ariaLabel={t('products.detail.prices.priceAria', { location: location.name })}
          placeholder={t('products.detail.prices.pricePlaceholder')}
          value={row.price}
          onChange={(e) => onChange({ price: e.target.value })}
          error={problem === 'priceInvalid' || problem === 'saleNeedsPrice'}
          disabled={disabled}
          size="small"
          fullWidth
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          dataTestId={`product-price-${location.storeId}`}
        />
      </Box>
      <Box role="cell">
        <TextField
          ariaLabel={t('products.detail.prices.saleAria', { location: location.name })}
          placeholder={t('products.detail.prices.salePlaceholder')}
          value={row.sale}
          onChange={(e) => onChange(withSaleStart(row, e.target.value, todayDay()))}
          error={problem === 'saleInvalid' || problem === 'saleNotBelowPrice'}
          disabled={disabled || !hasPrice}
          size="small"
          fullWidth
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          dataTestId={`product-sale-${location.storeId}`}
        />
      </Box>
      <Box role="cell">
        <TextField
          ariaLabel={t('products.detail.prices.saleStartsAria', { location: location.name })}
          type="date"
          value={hasSale ? row.saleStartsOn : ''}
          onChange={(e) => onChange({ saleStartsOn: e.target.value })}
          error={problem === 'saleEndsBeforeStart'}
          disabled={disabled || !hasSale}
          size="small"
          fullWidth
          dataTestId={`product-sale-starts-${location.storeId}`}
        />
      </Box>
      <Box role="cell">
        <TextField
          ariaLabel={t('products.detail.prices.saleEndsAria', { location: location.name })}
          type="date"
          value={hasSale ? row.saleEndsOn : ''}
          onChange={(e) => onChange({ saleEndsOn: e.target.value })}
          error={problem === 'saleEndInPast' || problem === 'saleEndsBeforeStart'}
          disabled={disabled || !hasSale}
          size="small"
          fullWidth
          slotProps={{ htmlInput: { min: row.saleStartsOn && row.saleStartsOn > todayDay() ? row.saleStartsOn : todayDay() } }}
          dataTestId={`product-sale-ends-${location.storeId}`}
        />
      </Box>
      <Box role="cell" data-testid={`product-in-stock-${location.storeId}`}>
        <Switch
          ariaLabel={t('products.detail.prices.inStockAria', { location: location.name })}
          checked={row.inStock}
          disabled={disabled || !hasPrice}
          onChange={(_e, checked) => onChange({ inStock: checked })}
          size="small"
        />
      </Box>
      <Box role="cell" data-testid={`product-shown-${location.storeId}`}>
        <Switch
          ariaLabel={t('products.detail.prices.shownAria', { location: location.name })}
          checked={row.shown}
          disabled={disabled || !hasPrice}
          onChange={(_e, checked) => onChange({ shown: checked })}
          size="small"
        />
      </Box>
    </Box>
  );
}
